import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  Radio,
  Select,
  Stack,
  TextInput
} from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  useGlobalFormState,
  useMediaQueryIndex,
  useModalState
} from '../../../helpers/hooks';
import PageView from '../../common/PageView';
import AppFlexbox from '../../common/AppFlexbox';
import {
  ECOM_PRODUCT_PRICING_TYPE_ENUM,
  ECOM_PRODUCT_STATUS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import AppStack from '../../common/AppStack';
import EditProductCard from '../products/EditProductCard';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import VendorCollectionProductSelectFormSection from '../vendors/VendorCollectionProductSelectFormSection';
import BundleDiscountEditControls from './BundleDiscountEditControls';
import AppText from '../../common/AppText';

const initialFormState = {
  name: '',
  description: '',
  fkEcomProductStatus: ECOM_PRODUCT_STATUS_ENUM.DRAFT,
  fkEcomPricingType: ECOM_PRODUCT_PRICING_TYPE_ENUM.PERCENTAGE_ADJUSTMENT,
  pricingAmount: null,
  ecomVendorProductMedia: [],
  ecomVendorBundleProducts: []
};

const BuyXGetYBundleEdit = ({
  backPath,
  onBackClick,
  duplicate,
  ecomVendorBundle
}) => {
  const { state } = useContext(VendorContext);
  const { isLaptopOrSmaller } = useMediaQueryIndex();
  const [dealType, setDealType] = useState(DEAL_TYPE_LIST[0].value);
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const {
    hasInitialized,
    hasUnsavedChanges,
    formState,
    isSubmitting,
    setFormState,
    resetFormState,
    submitFormState,
    ConfirmDiscardModal
  } = useGlobalFormState(initialFormState, {
    confirmDiscard: true,
    containerWidth: 1100
  });

  useEffect(() => {
    resetFormState({ ...initialFormState });
  }, []);

  return (
    <PageView
      backPath={backPath}
      formProps={{
        withFormControls: true,
        hasUnsavedChanges,
        isSubmitting,
        ...(!!ecomVendorBundle && !duplicate
          ? {
              onDelete: () => {
                onOpenModal(VIEW_ACTIONS_ENUM.DELETE, ecomVendorBundle);
              }
            }
          : {}),
        onSubmit: (e) => {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onBackClick={onBackClick}
      rightSection={
        !!ecomVendorBundle &&
        !duplicate && (
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <Button
              color="dark"
              component={Link}
              disabled={isSubmitting}
              radius="md"
              size="xs"
              to={`/vendor/product-bundles/${ecomVendorBundle.pkEcomVendorBundle}/duplicate`}
              type="button"
              variant="light"
            >
              Duplicate
            </Button>
          </AppFlexbox>
        )
      }
      title={ecomVendorBundle?.name ?? `Create Buy X Get Y bundle`}
      width={1100}
    >
      <AppFlexbox
        style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}
      >
        <AppStack
          style={{
            flex: 2,
            gap: 16
          }}
        >
          <EditProductCard>
            <AppStack>
              <AppStack style={{ gap: 8 }}>
                <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                  Customer buys (Buy X)
                </AppText>

                <AppStack style={{}}>
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    Select the products that the customer buys.
                  </AppText>

                  <AppText
                    style={{
                      fontSize: 14,
                      color: '#666'
                    }}
                  >
                    To qualify, the customer must purchase any of products (X)
                    to choose one of product (Y).
                  </AppText>
                </AppStack>
              </AppStack>

              <Divider />

              <AppStack style={{ gap: 8 }}>
                <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                  Customer gets (Get Y)
                </AppText>

                <AppStack style={{}}>
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    Select the products that the customer gets for free or at a
                    discounted price.
                  </AppText>
                </AppStack>
              </AppStack>
            </AppStack>
          </EditProductCard>

          <VendorCollectionProductSelectFormSection
            isSubmitting={isSubmitting}
            onChange={(products) => {
              setFormState({
                ...formState,
                ecomVendorBundleProducts: products
              });
            }}
            selectedProducts={formState.ecomVendorBundleProducts}
            subTitle="Select the products that the customer buys."
            title=""
          />

          <VendorCollectionProductSelectFormSection
            isSubmitting={isSubmitting}
            onChange={(products) => {
              setFormState({
                ...formState,
                ecomVendorBundleProducts: products
              });
            }}
            selectedProducts={formState.ecomVendorBundleProducts}
            subTitle="Select the products that the customer gets for free or at a discounted price."
            title="Customer gets (Get Y)"
          />

          <EditProductCard title="Discount options">
            <BundleDiscountEditControls
              bundleDiscount={[
                {
                  fkEcomProductPricingType: formState.fkEcomPricingType,
                  pricingAmount: formState.pricingAmount,
                  sort: 0
                }
              ]}
              disabled={isSubmitting}
              onChange={(updatedDiscount) =>
                setFormState({
                  ...formState,
                  fkEcomPricingType:
                    updatedDiscount[0].fkEcomProductPricingType,
                  pricingAmount: updatedDiscount[0].pricingAmount
                })
              }
            />
          </EditProductCard>

          <EditProductCard title="Bundle details">
            <AppStack>
              <TextInput
                disabled={isSubmitting}
                label="Title"
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    name: e.currentTarget.value
                  })
                }
                required
                value={formState.name}
              />
            </AppStack>
          </EditProductCard>
        </AppStack>

        <AppStack
          style={{ flex: 1, maxWidth: isLaptopOrSmaller ? 'unset' : 300 }}
        >
          <EditProductCard title="Status">
            <Select
              data={[
                { value: ECOM_PRODUCT_STATUS_ENUM.ACTIVE, label: 'Active' },
                { value: ECOM_PRODUCT_STATUS_ENUM.DRAFT, label: 'Draft' }
              ]}
              disabled={isSubmitting}
              onChange={(v) => {
                setFormState({
                  ...formState,
                  fkEcomProductStatus: v
                });
              }}
              value={formState.fkEcomProductStatus ?? ''}
            />
          </EditProductCard>
        </AppStack>
      </AppFlexbox>

      {ConfirmDiscardModal}
    </PageView>
  );
};

BuyXGetYBundleEdit.propTypes = {
  backPath: PropTypes.string,
  duplicate: PropTypes.bool,
  ecomVendorBundle: PropTypes.object,
  onBackClick: PropTypes.func
};

export default BuyXGetYBundleEdit;
